import { useState, useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowRight, Plus, Minus, Search, CheckCircle2 } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Reveal } from "@/components/site/Reveal";
import { BeforeAfterSlider } from "@/components/site/BeforeAfterSlider";
import { ServiceAreaMap } from "@/components/site/ServiceAreaMap";
import { SERVICES, MANIFESTO, SUBURBS, IMAGES, CONTACT } from "@/lib/content";
import { scrollToSection } from "@/hooks/useLenis";

const HERO_LINES = ["Precision Lawn Care.", "Sharp Edges.", "Total Restoration."];

const HeroImage = () => {
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const srx = useSpring(rx, { stiffness: 150, damping: 20 });
  const sry = useSpring(ry, { stiffness: 150, damping: 20 });
  const rotateX = useTransform(srx, [-0.5, 0.5], ["8deg", "-8deg"]);
  const rotateY = useTransform(sry, [-0.5, 0.5], ["-8deg", "8deg"]);
  const ref = useRef(null);

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    rx.set((e.clientY - r.top) / r.height - 0.5);
    ry.set((e.clientX - r.left) / r.width - 0.5);
  };
  const onLeave = () => { rx.set(0); ry.set(0); };

  return (
    <motion.div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      style={{ perspective: 1000 }}
      className="relative"
    >
      <motion.div style={{ rotateX, rotateY, transformStyle: "preserve-3d" }} className="relative rounded-[28px] overflow-hidden shadow-[0_40px_80px_-30px_rgba(11,15,12,0.5)]">
        <img src={IMAGES.hero} alt="Freshly striped lawn" className="w-full h-[380px] sm:h-[520px] object-cover" />
        <div className="absolute inset-0 ring-1 ring-inset ring-white/10 rounded-[28px]" />
      </motion.div>
    </motion.div>
  );
};

export default function Home({ onQuote }) {
  const [activeService, setActiveService] = useState(null);
  const [query, setQuery] = useState("");

  const filtered = SUBURBS.filter((s) => s.toLowerCase().includes(query.toLowerCase()));
  const matchInList = query && SUBURBS.some((s) => s.toLowerCase() === query.toLowerCase().trim());
  const covered = query.length > 1 ? filtered.length > 0 : true;

  return (
    <main className="overflow-x-hidden">
      {/* HERO */}
      <section id="hero" className="relative pt-32 pb-16 md:pt-40 md:pb-24 px-4 sm:px-6 lg:px-12">
        <div className="absolute top-24 -left-32 w-[420px] h-[420px] rounded-full bg-[#E8F0EB] blur-3xl -z-10" />
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
              className="inline-flex items-center gap-2 font-mono-t text-[11px] uppercase tracking-widest text-[#05C46B] border border-[#05C46B]/30 rounded-full px-4 py-1.5 mb-7"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#00E676] animate-pulse" /> Now booking · {CONTACT.area}
            </motion.div>

            <h1 data-testid="hero-heading" className="font-display font-extrabold tracking-tight text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.02]">
              {HERO_LINES.map((line, i) => (
                <span key={i} className="line-mask">
                  <motion.span
                    className="block"
                    initial={{ y: "110%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                  >
                    {i === 2 ? <span className="text-[#05C46B]">{line}</span> : line}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.8 }}
              className="mt-7 text-lg sm:text-xl text-[#52635A] max-w-xl leading-relaxed"
            >
              CBA Mowing takes pride in every lawn — mowing, edging, gardening, waste removal and full restoration, finished to a standard we'd want at home.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.8 }}
              className="mt-9 flex flex-wrap items-center gap-3"
            >
              <button data-testid="hero-cta-button" onClick={onQuote} className="inline-flex items-center gap-2 rounded-full bg-[#0B0F0C] text-white font-semibold px-7 py-4 hover:bg-[#05C46B] transition-colors duration-300">
                Get a Free Quote <ArrowRight className="w-4 h-4" />
              </button>
              <button data-testid="hero-services-link" onClick={() => scrollToSection("services")} className="inline-flex items-center gap-2 rounded-full border border-black/15 font-semibold px-7 py-4 hover:border-[#05C46B] hover:text-[#05C46B] transition-colors duration-300">
                View Services
              </button>
            </motion.div>
          </div>

          <div className="lg:col-span-5">
            <HeroImage />
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="border-y border-black/10 bg-[#0B0F0C] py-5 overflow-hidden">
        <div className="marquee-track">
          {[0, 1].map((k) => (
            <div key={k} className="flex items-center gap-8 pr-8">
              {["Lawn Mowing", "Edge Cutting", "Gardening Maintenance", "Waste Removal", "Restoration", "Transformation", "Precision"].map((t) => (
                <span key={t} className="flex items-center gap-8 font-mono-t text-sm uppercase tracking-widest text-white">
                  {t} <span className="text-[#00E676]">✦</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* MANIFESTO */}
      <section id="manifesto" className="py-20 md:py-32 px-4 sm:px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <p className="font-mono-t text-[11px] uppercase tracking-widest text-[#05C46B]">Our Manifesto</p>
            <h2 className="mt-3 font-display font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl max-w-2xl">
              Three principles behind every blade of grass.
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 lg:grid-cols-3 gap-8">
            {MANIFESTO.map((m, i) => (
              <Reveal key={m.num} delay={i * 0.1}>
                <div className="group h-full border-t-2 border-[#0B0F0C] pt-6">
                  <div className="flex items-baseline justify-between">
                    <span className="font-mono-t text-sm text-[#52635A]">Chapter {m.num}</span>
                    <ArrowUpRight className="w-5 h-5 text-[#05C46B] opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all duration-300" />
                  </div>
                  <h3 className="font-editorial text-3xl mt-6">{m.title}</h3>
                  <p className="mt-4 text-[#52635A] leading-relaxed">{m.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="py-20 md:py-32 px-4 sm:px-6 lg:px-12 bg-[#F4F7F5]">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
              <div>
                <p className="font-mono-t text-[11px] uppercase tracking-widest text-[#05C46B]">What We Do</p>
                <h2 className="mt-3 font-display font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl max-w-2xl">Flagship services</h2>
              </div>
              <p className="text-[#52635A] max-w-sm">Tap any service for a full breakdown of what's included and how it works.</p>
            </div>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-8">
            {SERVICES.map((s, i) => (
              <Reveal key={s.id} delay={i * 0.1}>
                <div data-testid={`service-card-${s.id}`} className="group bg-white rounded-3xl overflow-hidden border border-black/5 hover:shadow-[0_30px_60px_-25px_rgba(11,15,12,0.35)] transition-shadow duration-500 flex flex-col h-full">
                  <div className="relative h-52 overflow-hidden">
                    <img src={s.image} alt={s.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[900ms] ease-out" />
                    <span className="absolute top-4 left-4 font-mono-t text-xs bg-white/90 backdrop-blur px-3 py-1.5 rounded-full">{s.num}</span>
                  </div>
                  <div className="p-7 flex flex-col flex-1">
                    <h3 className="font-display font-bold text-xl tracking-tight">{s.title}</h3>
                    <p className="mt-3 text-[#52635A] leading-relaxed flex-1">{s.short}</p>
                    <button
                      data-testid={`service-more-info-button-${s.id}`}
                      onClick={() => setActiveService(s)}
                      className="mt-6 inline-flex items-center gap-2 font-semibold text-[#0B0F0C] link-underline w-fit"
                    >
                      More information <ArrowUpRight className="w-4 h-4 text-[#05C46B]" />
                    </button>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* BEFORE & AFTER */}
      <section id="before-after" className="py-20 md:py-32 px-4 sm:px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <p className="font-mono-t text-[11px] uppercase tracking-widest text-[#05C46B]">The Proof</p>
            <h2 className="mt-3 font-display font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl max-w-2xl">Before & after</h2>
            <p className="mt-4 text-[#52635A] max-w-xl">Drag the slider to reveal the transformation. Empty frames below are ready for your own photos.</p>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-8">
            <Reveal>
              <BeforeAfterSlider testId="before-after-slider-1" before={IMAGES.ba1_before} after={IMAGES.ba1_after} />
              <p className="mt-3 font-mono-t text-[11px] uppercase tracking-widest text-[#52635A]">Case 01 · Full lawn revival · North Lakes</p>
            </Reveal>
            <Reveal delay={0.1}>
              <BeforeAfterSlider testId="before-after-slider-2" before={IMAGES.ba2_before} after={IMAGES.ba2_after} />
              <p className="mt-3 font-mono-t text-[11px] uppercase tracking-widest text-[#52635A]">Case 02 · Overgrowth restoration · Mango Hill</p>
            </Reveal>
          </div>

          <Reveal delay={0.15}>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-8">
              <div>
                <BeforeAfterSlider testId="before-after-slider-3" before={IMAGES.ba3_before} after={IMAGES.ba3_after} />
                <p className="mt-3 font-mono-t text-[11px] uppercase tracking-widest text-[#52635A]">Case 03 · Curb edge restoration · Mango Hill</p>
              </div>
              <div>
                <BeforeAfterSlider testId="before-after-slider-4" before={IMAGES.ba4_before} after={IMAGES.ba4_after} />
                <p className="mt-3 font-mono-t text-[11px] uppercase tracking-widest text-[#52635A]">Case 04 · Front lawn tidy-up · Mango Hill</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SERVICE AREA MAP */}
      <section id="service-area" className="py-20 md:py-32 px-4 sm:px-6 lg:px-12 bg-[#0B0F0C] text-white">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div>
            <p className="font-mono-t text-[11px] uppercase tracking-widest text-[#00E676]">Coverage</p>
            <h2 className="mt-3 font-display font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl">Where we mow</h2>
            <p className="mt-4 text-[#9EB0A5] max-w-md">Check if your suburb is inside our service radius. We're always expanding — if you're just outside, ask us anyway.</p>

            <div className="mt-8 relative max-w-md">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9EB0A5]" />
              <Input
                data-testid="suburb-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Enter your suburb…"
                className="pl-11 h-14 rounded-full bg-white/5 border-white/15 text-white placeholder:text-[#9EB0A5] focus-visible:ring-[#00E676]"
              />
            </div>

            {query.length > 1 && (
              <div data-testid="coverage-badge" className={`mt-4 inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold ${covered ? "bg-[#00E676] text-[#0B0F0C]" : "bg-white/10 text-white"}`}>
                <CheckCircle2 className="w-4 h-4" />
                {covered ? "In service area — book now!" : "Just outside — call us to confirm"}
              </div>
            )}

            <div className="mt-8 flex flex-wrap gap-2">
              {(query ? filtered : SUBURBS).slice(0, 12).map((s) => (
                <span key={s} data-testid={`suburb-chip-${s.toLowerCase().replace(/\s/g, "-")}`} className="font-mono-t text-[11px] uppercase tracking-widest border border-white/15 text-[#E8F0EB] rounded-full px-3 py-1.5">
                  {s}
                </span>
              ))}
              {query && filtered.length === 0 && <span className="text-sm text-[#9EB0A5]">No exact match — we may still cover you.</span>}
            </div>
          </div>

          {/* Interactive map */}
          <ServiceAreaMap />
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="px-4 sm:px-6 lg:px-12 py-20">
        <Reveal>
          <div className="max-w-7xl mx-auto rounded-[32px] bg-[#E8F0EB] px-8 sm:px-14 py-16 text-center relative overflow-hidden">
            <div className="absolute -top-16 -right-10 w-64 h-64 rounded-full bg-[#00E676]/20 blur-2xl" />
            <p className="font-mono-t text-[11px] uppercase tracking-widest text-[#05C46B]">Ready when you are</p>
            <h2 className="mt-4 font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-6xl max-w-3xl mx-auto">Let's transform your lawn.</h2>
            <button data-testid="cta-strip-quote" onClick={onQuote} className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#0B0F0C] text-white font-semibold px-8 py-4 hover:bg-[#05C46B] transition-colors duration-300">
              Get Your Free Quote <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </Reveal>
      </section>

      {/* SERVICE DETAIL SHEET */}
      <Sheet open={!!activeService} onOpenChange={(o) => !o && setActiveService(null)}>
        <SheetContent data-testid="service-detail-sheet" className="w-full sm:max-w-lg overflow-y-auto">
          {activeService && (
            <>
              <div className="relative h-56 -mx-6 -mt-6 mb-6 overflow-hidden">
                <img src={activeService.image} alt={activeService.title} className="w-full h-full object-cover" />
                <span className="absolute top-4 left-4 font-mono-t text-xs bg-white/90 px-3 py-1.5 rounded-full">Chapter {activeService.num}</span>
              </div>
              <SheetHeader>
                <SheetTitle className="font-display text-2xl font-extrabold tracking-tight text-left">{activeService.title}</SheetTitle>
              </SheetHeader>
              <p className="mt-3 text-[#52635A] leading-relaxed">{activeService.short}</p>
              <p className="mt-8 font-mono-t text-[11px] uppercase tracking-widest text-[#05C46B]">What's included</p>
              <ul className="tick-list mt-4 space-y-3">
                {activeService.points.map((p) => (
                  <li key={p} className="text-[#0B0F0C]">{p}</li>
                ))}
              </ul>
              <button
                data-testid="sheet-quote-button"
                onClick={() => { setActiveService(null); onQuote(activeService.id); }}
                className="mt-10 w-full rounded-full bg-[#0B0F0C] text-white font-semibold py-3.5 hover:bg-[#05C46B] transition-colors duration-300"
              >
                Request a quote for this service
              </button>
            </>
          )}
        </SheetContent>
      </Sheet>
    </main>
  );
}
